// Answer-condition helpers shared by certificate approval and process triggers.

export type ConditionOperator = 'equals' | 'notEquals' | 'contains' | 'greaterThan' | 'lessThan';

export interface AnswerCondition {
  formId: string;
  questionId: string;
  operator: ConditionOperator;
  expectedValue: any;
}

export interface DesignQuestion {
  id: string;
  label: string;
  type: string;
  options?: string[];
}

// Types that are display-only or file-based — not useful for conditions
export const NON_CONDITION_TYPES = new Set([
  'Add Title', 'Add Description', 'Add URL',
  'Add Document', 'Add Image', 'Add Video',
  'Upload', 'Signature', 'Flight Plan',
]);

export function getOperatorsForType(type: string): ConditionOperator[] {
  switch (type) {
    case 'Dropdown':
      return ['equals', 'notEquals'];
    case 'Checkbox':
      return ['contains', 'equals'];
    case 'Number':
    case 'Add Calculation':
      return ['equals', 'notEquals', 'greaterThan', 'lessThan'];
    case 'Date':
    case 'DateTime':
    case 'Time':
      return ['equals', 'greaterThan', 'lessThan'];
    case 'Date Range':
      return ['equals'];
    default:
      return ['equals', 'notEquals', 'contains'];
  }
}

export function getDefaultOperator(type: string): ConditionOperator {
  return getOperatorsForType(type)[0];
}

export function getInputType(type: string): string {
  switch (type) {
    case 'Number':
    case 'Add Calculation':
      return 'number';
    case 'Date':
      return 'date';
    case 'DateTime':
      return 'datetime-local';
    case 'Time':
      return 'time';
    case 'Date Range':
      return 'date';
    default:
      return 'text';
  }
}

/** Labelled questions of a form design, optionally filtered by a predicate. */
export function extractDesignQuestions(
  design: unknown,
  include: (question: DesignQuestion) => boolean = () => true,
): DesignQuestion[] {
  if (!Array.isArray(design)) return [];
  const questions: DesignQuestion[] = [];
  for (const section of design) {
    for (const question of section?.questions ?? []) {
      if (!question?.id || !question?.label) continue;
      const item: DesignQuestion = {
        id: question.id,
        label: question.label,
        type: question.type || 'Short Text',
        options: question.options,
      };
      if (include(item)) questions.push(item);
    }
  }
  return questions;
}

/** Questions an answer condition can test. */
export const extractConditionQuestions = (design: unknown) =>
  extractDesignQuestions(design, (q) => !NON_CONDITION_TYPES.has(q.type));
